import { useAuth } from '@/store/useAuth';

import { api } from './api';

export type SourceKind =
  | 'perfil_social'
  | 'feed'
  | 'periodico'
  | 'changelog'
  | 'cluster'
  | 'documento';
export type TriggerKind = 'novo_item' | 'limiar' | 'ator' | 'cruzamento' | 'spike' | 'documento';
export type Rota = 'alerta' | 'card' | 'auto';
export type EventEstado = 'pendente' | 'rascunho' | 'produzido' | 'dispensado';

export interface Vigia {
  slug: string;
  nome: string;
  source: {
    kind: SourceKind;
    alvo?: string;
    url?: string;
    query?: string;
    urls?: string[];
    queries?: string[];
    reddit?: string;
    hn?: boolean;
    itens?: number;
    // De quanto em quanto tempo o worker busca esta fonte ("15min", "1h", "8h", "diário").
    intervalo?: string;
    // Fonte primária (documento): com que cadência as matérias de UM documento
    // vão ao ar. por_dia=0 herda o teto de assunto/dia da política da casa.
    cadencia?: { por_dia?: number; intervalo_min?: number };
  };
  trigger: { kind: TriggerKind; limiar?: number; n_fontes?: number };
  brief: string;
  rota: Rota;
  pub: { sites: string[]; categoria?: string; status?: 'publish' | 'draft' };
  ativo: boolean;
  ordem: number;
  last_fired_at?: string | null;
}

export type VigiaPatch = Partial<Omit<Vigia, 'slug'>>;
export interface VigiaCreate {
  slug: string;
  nome: string;
  source: Vigia['source'];
  trigger: Vigia['trigger'];
  brief?: string;
  rota?: Rota;
  pub?: Vigia['pub'];
}

export interface Triage {
  relevante?: boolean;
  relevancia?: string;
  angulo?: string;
  motivo?: string;
  model?: string;
  cost_usd?: number;
  // Fonte primária: o que prova, no documento, que esta pauta existe.
  sustentacao?: string;
  fatos?: string[];
  falta_apurar?: string[];
  cuidados?: string[];
  natureza?: string;
  rota_erro?: string;
}

/* ─── Fonte primária: o documento e o plano de pauta ───────────────────────── */
export type DocEstado =
  | 'recebido'
  | 'extraindo'
  | 'avaliando'
  | 'recusado'
  | 'lendo'
  | 'analisado'
  | 'produzindo'
  | 'concluido'
  | 'falhou';

export interface PautaPlanejada {
  titulo: string;
  angulo: string;
  sustentacao: string;
  fatos: string[];
  falta_apurar: string[];
  forca: string;
}

export interface VigiaDocumento {
  id: string;
  vigia_slug: string;
  nome: string;
  origem?: string | null;
  tipo: 'pdf' | 'texto' | 'video';
  paginas: number;
  chars: number;
  estado: DocEstado;
  progresso: { etapa?: string; pct?: number; feitos?: number; total?: number; mensagem?: string };
  plano: {
    eh_noticia?: boolean;
    motivo?: string;
    natureza?: string;
    resumo?: string;
    pautas?: PautaPlanejada[];
    cuidados?: string[];
    cadencia?: { por_dia: number; intervalo_min: number; total: number; dias: number };
    custo_total_usd?: number;
  };
  meta: { duracao_seg?: number; transcricao_usd?: number };
  created_at?: string | null;
  repetido?: boolean;
}

export interface VigiaEvent {
  id: string;
  vigia_slug: string;
  vigia_nome: string;
  trigger_kind: TriggerKind;
  titulo: string;
  url?: string | null;
  resumo?: string | null;
  origem?: string | null;
  metrics: {
    curtidas?: number;
    reposts?: number;
    comentarios?: number;
    limiar?: number;
    spike?: boolean;
  };
  fontes: string[];
  triage: Triage;
  rota: Rota;
  estado: EventEstado;
  wp_id?: number | null;
  wp_link?: string | null;
  documento_id?: string | null;
  // Hora marcada pela cadência (nulo = já está liberada / já saiu).
  publicar_em?: string | null;
  created_at?: string | null;
}

export interface Resumo {
  total: number;
  ativos: number;
  produzidos: number;
  pendentes: number;
}

export interface TickResult {
  slug: string;
  novos: number;
  eventos: VigiaEvent[];
  erro?: string;
}

export const vigiasApi = {
  list: (): Promise<Vigia[]> => api.get<Vigia[]>('/vigias'),
  get: (slug: string): Promise<Vigia> => api.get<Vigia>(`/vigias/${slug}`),
  create: (body: VigiaCreate): Promise<Vigia> => api.post<Vigia>('/vigias', body),
  patch: (slug: string, patch: VigiaPatch): Promise<Vigia> =>
    api.put<Vigia>(`/vigias/${slug}`, patch),
  remove: (slug: string): Promise<unknown> => api.delete<unknown>(`/vigias/${slug}`),
  resumo: (): Promise<Resumo> => api.get<Resumo>('/vigias/resumo'),
  eventos: (params?: { estado?: string; trigger?: string }): Promise<VigiaEvent[]> => {
    const q = new URLSearchParams();
    if (params?.estado) q.set('estado', params.estado);
    if (params?.trigger) q.set('trigger', params.trigger);
    const qs = q.toString();
    return api.get<VigiaEvent[]>(`/vigias/eventos${qs ? `?${qs}` : ''}`);
  },
  tickAll: (): Promise<{ total: number; vigias: { slug: string; novos: number }[] }> =>
    api.post('/vigias/tick'),
  tick: (slug: string): Promise<TickResult> => api.post<TickResult>(`/vigias/${slug}/tick`),
  produzir: (id: string): Promise<{ ok: boolean; wp_link?: string; event: VigiaEvent }> =>
    api.post(`/vigias/eventos/${id}/produzir`),
  dispensar: (id: string): Promise<{ ok: boolean }> => api.post(`/vigias/eventos/${id}/dispensar`),

  /* ─── Fonte primária ───────────────────────────────────────────────────── */
  documentos: (vigia?: string): Promise<VigiaDocumento[]> =>
    api.get<VigiaDocumento[]>(`/vigias/documentos${vigia ? `?vigia=${vigia}` : ''}`),

  documento: (id: string): Promise<VigiaDocumento> =>
    api.get<VigiaDocumento>(`/vigias/documentos/${id}`),

  /**
   * Entrega um documento (PDF, vídeo ou texto colado) a uma vigia.
   *
   * fetch cru porque FormData não pode levar `Content-Type: application/json`
   * — mesmo motivo do upload de mídia. O token continua obrigatório.
   * Responde 202: o id volta na hora e o avanço é lido em `documento(id)`.
   */
  enviarDocumento: async (
    slug: string,
    entrada: { arquivo?: File; texto?: string; nome?: string; origem?: string },
  ): Promise<VigiaDocumento> => {
    const form = new FormData();
    if (entrada.arquivo) form.append('arquivo', entrada.arquivo);
    if (entrada.texto) form.append('texto', entrada.texto);
    if (entrada.nome) form.append('nome', entrada.nome);
    if (entrada.origem) form.append('origem', entrada.origem);
    const token = useAuth.getState().token;
    const resp = await fetch(`/api/v1/vigias/${slug}/documento`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    if (!resp.ok) {
      const corpo = (await resp.json().catch(() => null)) as { detail?: string } | null;
      throw new Error(corpo?.detail ?? `falha ao enviar o documento (${resp.status})`);
    }
    return resp.json() as Promise<VigiaDocumento>;
  },
};
